/**
 * Package Transfer Modal Component
 * Modal for transferring packages (cases/pallets) to another user
 */

import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { packagingService, Package } from '../services/packaging.service';
import { userService } from '../services/user.service';
import Modal, { ModalFooter } from './ui/Modal';
import { Button, Select, Alert } from './ui';

export interface PackageTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  packageData: Package;
  totalUnits: number;
  onSuccess: () => void;
}

export default function PackageTransferModal({
  isOpen,
  onClose,
  packageData,
  totalUnits,
  onSuccess,
}: PackageTransferModalProps) {
  const { user } = useAuthStore();
  const [recipients, setRecipients] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadRecipients();
    }
  }, [isOpen]);

  const loadRecipients = async () => {
    if (!user?.manufacturerId) return;

    try {
      setLoadingRecipients(true);
      const result = await userService.getApprovedUsers(user.manufacturerId);

      if (result.success && result.data) {
        const options = result.data
          .filter((u: any) => u.id !== user.id) // Exclude current user
          .map((u: any) => ({
            value: u.id,
            label: `${u.fullName} (${u.role})`,
          }));
        setRecipients(options);
      }
    } catch (err: any) {
      console.error('Failed to load recipients:', err);
      setError('Failed to load available recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedRecipient) {
      setError('Please select a recipient');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await packagingService.transferPackage(packageData.qrToken, selectedRecipient);

      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.error || 'Failed to transfer package');
      }
    } catch (err: any) {
      console.error('Failed to transfer package:', err);
      setError(err.response?.data?.error || 'Failed to transfer package');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transfer Package" size="md">
      <div className="space-y-4">
        {/* Package Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-blue-900">Package Type</span>
            <span
              className={`px-3 py-1 rounded font-bold text-white ${
                packageData.packageType === 'PALLET' ? 'bg-purple-600' : 'bg-blue-600'
              }`}
            >
              {packageData.packageType}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-medium text-blue-900">Total Units</span>
            <span className="text-2xl font-bold text-blue-600">{totalUnits}</span>
          </div>
        </div>

        {/* Warning */}
        <Alert variant="warning">
          <p className="font-medium mb-1">Bulk Transfer</p>
          <p className="text-sm">
            This will transfer all {totalUnits} units contained in this {packageData.packageType.toLowerCase()}{' '}
            to the selected recipient. This action cannot be undone.
          </p>
        </Alert>

        {/* Error */}
        {error && <Alert variant="error">{error}</Alert>}

        {/* Recipient Selection */}
        {loadingRecipients ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <Select
            label="Select Recipient"
            options={recipients}
            value={selectedRecipient}
            onChange={(e) => setSelectedRecipient(e.target.value)}
            placeholder="Choose a recipient..."
            required
            disabled={loading}
          />
        )}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleTransfer} disabled={loading || !selectedRecipient}>
          {loading ? 'Transferring...' : 'Confirm Transfer'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
